// Account section: the computers signed in with the cmx CLI (one long-lived token each), with Revoke,
// and the one-line install to add another. With none yet, the three steps to the first (full guide: /guide).

import { CodeRow } from '../ui/CodeRow';
import { useCallback, useEffect, useState } from 'react';
import { cli, installCommand, type CliDevice } from '../api';
import { Link } from '../router';
import { ago, fullDate } from './Shell';
import './cli.css';
import { escCloses } from '../ui/keys';
import { Ic } from '../ui/ds';
import { IconDeviceLaptop, IconFolder, IconPlus, IconTerminal } from '@tabler/icons-react';
import { Button, buttonClass } from '../ui/Button';
import { openApps } from '../ui/GetApps';

export { CopyButton } from '../ui/CodeRow';

// the app, the Finder link's token ("<Mac> (Finder)", MachineInFinder) or the CLI: the Mac's AccountPane
function DeviceRow({ d, onRevoked }: { d: CliDevice; onRevoked: (id: string) => void }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revoke = async () => {
    setBusy(true);
    setError(null);
    const r = await cli.revoke(d.id);
    setBusy(false);
    if (r.ok || r.status === 404) onRevoked(d.id);
    else setError(r.error);
  };
  return (
    <tr data-testid="cli-device-row" data-device-id={d.id} data-device-name={d.name}>
      <td className="cell-head">
        <span className="cli-name">
          <span className="cli-name-icon" aria-hidden="true">
            <Ic icon={d.kind === 'app' ? IconDeviceLaptop : d.name.endsWith('(Finder)') ? IconFolder : IconTerminal} size={16} />
          </span>
          {d.name}
        </span>
      </td>
      <td className="muted" data-label="Signed in" title={fullDate(d.created)}>
        {ago(d.created)}
      </td>
      <td className="muted" data-label="Last used" title={fullDate(d.lastUsed)}>
        {ago(d.lastUsed)}
      </td>
      <td className="cell-actions">
        <div className="row-btns" onKeyDown={escCloses(() => setConfirming(false))}>
          {error && <span className="confirm-text">{error}</span>}
          {confirming ? (
            <>
              <span className="confirm-text">Revoke?</span>
              <button className="btn btn-sm btn-danger-solid" autoFocus disabled={busy} onClick={() => void revoke()} data-testid="cli-revoke-confirm">
                {busy ? 'Revoking…' : 'Revoke'}
              </button>
              <button className="btn btn-sm btn-ghost" onClick={() => setConfirming(false)}>
                Cancel
              </button>
            </>
          ) : (
            <button className="btn btn-sm btn-ghost btn-ghost-danger" onClick={() => setConfirming(true)} data-testid="cli-revoke">
              Revoke
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}


/** The three steps to a first signed-in computer: the empty state of the devices. */
function CliSteps({ install }: { install: string }) {
  return (
    <ol className="cli-steps" data-testid="cli-guide">
      <li>
        <span className="cli-step-n" aria-hidden="true">1</span>
        <div className="cli-step-body">
          <span className="cli-step-title">Install and sign in (macOS or Linux)</span>
          <CodeRow text={install} codeTestid="cli-install-cmd" copyTestid="cli-copy-install" />
          <span className="muted">Approve it in the browser page it opens; it then shows up here.</span>
        </div>
      </li>
      {/* every step the same shape: what to do, the command to copy, one plain line under it */}
      <li>
        <span className="cli-step-n" aria-hidden="true">2</span>
        <div className="cli-step-body">
          <span className="cli-step-title">Start working</span>
          <CodeRow text="cmx" codeTestid="cli-start-cmd" copyTestid="cli-copy-start" />
          <span className="cli-leave muted" data-testid="cli-leave">
            To leave, press <kbd className="cli-keys">Enter ~ .</kbd> in turn. The session keeps running; run it again to come back.
          </span>
        </div>
      </li>
      <li>
        <span className="cli-step-n" aria-hidden="true">3</span>
        <div className="cli-step-body">
          <span className="cli-step-title">Using the ChatGPT, Claude or VS Code app?</span>
          <CodeRow text="cmx connect" codeTestid="cli-connect-cmd" copyTestid="cli-copy-connect" />
          <span className="muted">Their agents run on your cloud machine and keep working while this computer sleeps.</span>
        </div>
      </li>
    </ol>
  );
}

export function CliDevicesCard() {
  const [devices, setDevices] = useState<CliDevice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    const r = await cli.devices();
    if (r.ok) {
      setDevices(r.data.devices ?? []);
      setError(null);
    } else setError(r.error);
  }, []);
  useEffect(() => {
    void load();
    const t = setInterval(() => document.visibilityState === 'visible' && void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  const install = installCommand();
  return (
    <section className="card card-cli" aria-labelledby="cli-title" data-testid="cli-devices">
      <div className="card-head">
        <div className="cli-head-text">
          <div className="card-title">
            <h2 id="cli-title">Command line</h2>
          </div>
          <span className="muted small-print">Your sessions in your own terminal app: its fonts, keys and scrollback</span>
        </div>
        <div className="cli-head-actions">
          <Link to="/guide#cli" className={buttonClass('link')} data-testid="cli-full-guide">
            See command guide
          </Link>
          <Button size="sm" icon={<IconPlus size={16} stroke={1.5} aria-hidden />} onClick={() => openApps()} data-testid="devices-add">
            Add a device
          </Button>
        </div>
      </div>
      {devices && devices.length === 0 ? (
        <CliSteps install={install} />
      ) : (
        <>
          {devices && (
            <div className="cli-add">
              <span className="cli-add-label">Add a computer</span>
              <CodeRow text={install} codeTestid="cli-install-cmd" copyTestid="cli-copy-install" />
            </div>
          )}
          <h3 className="cli-ref-title">
            Signed-in devices{devices && <span className="count">{devices.length}</span>}
          </h3>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <div className="table-wrap table-cards">
            {!devices && !error && <div className="skeleton" aria-busy="true" aria-label="Loading devices" />}
            {devices && (
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Device</th>
                    <th scope="col">Signed in</th>
                    <th scope="col">Last used</th>
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => (
                    <DeviceRow key={d.id} d={d} onRevoked={(id) => setDevices((cur) => (cur ?? []).filter((x) => x.id !== id))} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </section>
  );
}
